import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Box, InputBase, Typography } from '@lyearn/ui';
import { blurOnKeyDown } from '@/modules/org-hierarchy/utils';

import { useMicroSkillActions } from '../../context/MicroSkillsContext';
import { MicroSkillCellContainerProps, MicroSkillCellProps } from './types';

const MicroSkillEditCell = (props: MicroSkillCellProps) => {
  const { microSkill } = props;
  const { t } = useTranslation();
  const [value, setValue] = useState(props.microSkill.name);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    actions,
    uiState: { isMicroSkillEditableState },
  } = useMicroSkillActions(microSkill);

  useEffect(() => {
    if (inputRef.current && isMicroSkillEditableState) {
      inputRef.current.focus();
    }
  }, [isMicroSkillEditableState]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
      actions?.onEditMicroSkill({ name: event.target.name });
    },
    [actions],
  );

  const onBlur = useCallback(() => {
    if (value.trim() === microSkill.name.trim()) {
      actions?.onRemoveMicroSkillEditState();
      return;
    }
    actions?.onEditMicroSkill({ name: value.trim() });
  }, [value, microSkill.name, actions]);

  return (
    <InputBase
      multiline
      className="body-short-01 w-full py-12"
      inputRef={inputRef}
      placeholder={t('SkillMatrix:MicroSkills.MicroskillName')}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      onKeyDown={blurOnKeyDown}
    />
  );
};

const MicroSkillCell = (props: MicroSkillCellProps) => {
  const { microSkill } = props;

  const { uiState } = useMicroSkillActions(microSkill);

  if (uiState?.canEdit) {
    return <MicroSkillEditCell microSkill={microSkill} />;
  }

  return (
    <Typography className="py-16" variant="body-short-01">
      {microSkill.name}
    </Typography>
  );
};

const MicroSkillCellContainer = (props: MicroSkillCellContainerProps) => {
  const { rowData } = props;
  const { type } = rowData;
  if (type !== 'MICRO_SKILL') {
    return null;
  }
  const { microSkill } = rowData;
  return (
    <Box className="w-full pe-8">
      <MicroSkillCell microSkill={microSkill} />
    </Box>
  );
};

export default MicroSkillCellContainer;
